import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import HotelIcon from '@mui/icons-material/Hotel';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import uuid from 'react-uuid';
import moment from 'moment';
import 'moment/locale/en-gb';
import { useConfig } from '../../../api/api';
import { tokens } from '../../../global/theme/tokens';
import { numberToString } from '../../special/numberConverter';

const MEALS = [
  { key: 'breakfastProvided', label: 'Breakfast' },
  { key: 'lunchProvided', label: 'Lunch' },
  { key: 'dinnerProvided', label: 'Dinner' },
];

export const rateTypeOptions = [
  { value: 'full', label: 'Full day' },
  { value: 'partial', label: 'Arrival / departure day' },
  { value: 'none', label: 'No allowance' },
];

export function emptyDay(date = '') {
  return {
    uuid: uuid(),
    date,
    rateType: 'full',
    breakfastProvided: false,
    lunchProvided: false,
    dinnerProvided: false,
    overnightFlatRateClaimed: false,
  };
}

/**
 * The meal allowance (Verpflegungsmehraufwand), one compact line per day: date,
 * rate type, meal chips and the day's amount — separated by hairlines instead
 * of one card per day, so a week-long trip stays scannable.
 *
 * The set of days belongs to the employee: a trip can outlast the driving that
 * bookends it (travel out, work a week, take holiday on the way home), so days
 * are added, removed and re-rated freely. The initial set is suggested
 * automatically once the travel log has a departure and an arrival.
 *
 * Meals are toggle chips: a filled chip means the employer provided that meal
 * and its share is deducted. Amounts come from the server preview and are
 * display-only.
 */
const TypeTravelExpenseDays = ({
  field,
  dataCollection,
  updateValidationOnChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const dateFormat = config?.general?.dateOutputFormat || 'DD.MM.YYYY';

  // "en-gb" uses Monday as the first day of the week (same as TypeDate).
  useEffect(() => {
    moment.locale('en-gb');
  }, []);

  const days = dataCollection[field.key] || [];
  const currencySymbol = field.currencySymbol ?? '';

  const computedByDate = {};
  (field.preview?.days || []).forEach((day) => {
    computedByDate[day.date] = day;
  });
  const computed = (date) => computedByDate[date] || {};

  function commit(newDays) {
    updateValidationOnChange(field.key, newDays);
  }

  function handleChange(index, key, value) {
    const newDays = [...days];
    newDays[index] = { ...newDays[index], [key]: value };
    commit(newDays);
  }

  function handleAdd() {
    // Default the new day to the one after the last, so a stay extends
    // naturally. The first day falls back to the trip start, then to today, so
    // every row is priced the moment it appears.
    const last = days[days.length - 1];
    const nextDate = last?.date
      ? moment(last.date).add(1, 'day').format('YYYY-MM-DD')
      : moment(field.preview?.startTime || undefined).format('YYYY-MM-DD');
    commit([...days, emptyDay(nextDate)]);
  }

  function handleDelete(index) {
    commit(days.filter((_, i) => i !== index));
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box
        sx={{
          gridColumn: field.colspan
            ? `span ${field.colspan} / span ${field.colspan}`
            : 'auto',
        }}
        className="w-full flex flex-col"
      >
        {days.length === 0 && (
          <Typography variant="body2" className="opacity-60 px-3 py-3">
            {t(
              'No allowance days yet. They are suggested from the travel log once departure and arrival are set, or add one yourself.',
            )}
          </Typography>
        )}

        {days.length > 0 && (
          <Box className="overflow-x-auto">
            <Box className="flex flex-col" sx={{ minWidth: '560px' }}>
              {days.map((day, index) => (
                <Box
                  key={day.uuid || day.date}
                  className="px-3 py-2"
                  sx={{
                    display: 'grid',
                    // Fixed slots — including one for the overnight toggle —
                    // so a day gaining the overnight option cannot rewrap the
                    // row and shift the layout.
                    gridTemplateColumns: '140px 170px minmax(0, 1fr) 34px 64px 28px',
                    gap: '8px',
                    alignItems: 'center',
                    ...(index > 0
                      ? { borderTop: `1px solid ${colors.selected}` }
                      : {}),
                  }}
                >
                  <DatePicker
                    format={dateFormat}
                    value={day.date ? moment(day.date) : null}
                    onChange={(newValue) =>
                      handleChange(
                        index,
                        'date',
                        newValue && newValue.isValid()
                          ? newValue.format('YYYY-MM-DD')
                          : '',
                      )
                    }
                    slotProps={{ textField: { size: 'small' } }}
                    sx={{ minWidth: 0 }}
                  />

                  <TextField
                    select
                    size="small"
                    value={day.rateType || 'full'}
                    onChange={(e) => handleChange(index, 'rateType', e.target.value)}
                    sx={{ minWidth: 0 }}
                  >
                    {rateTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.label)}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Box className="flex flex-wrap items-center gap-1.5 min-w-0">
                    <Typography variant="caption" className="opacity-60">
                      {t('Provided')}:
                    </Typography>

                    {MEALS.map((meal) => {
                      const provided = Boolean(day[meal.key]);
                      return (
                        <Chip
                          key={meal.key}
                          size="small"
                          label={t(meal.label)}
                          variant={provided ? 'filled' : 'outlined'}
                          color={provided ? 'info' : 'default'}
                          onClick={() => handleChange(index, meal.key, !provided)}
                          sx={{ '&:active': { transform: 'scale(0.96)' } }}
                        />
                      );
                    })}
                  </Box>

                  {computed(day.date).canClaimOvernight ? (
                    <Tooltip
                      title={`${t('Accommodation allowance')} — ${t(
                        'Flat rate for the following night, instead of an actual hotel cost.',
                      )}`}
                    >
                      <IconButton
                        size="small"
                        color={day.overnightFlatRateClaimed ? 'success' : 'default'}
                        onClick={() =>
                          handleChange(
                            index,
                            'overnightFlatRateClaimed',
                            !day.overnightFlatRateClaimed,
                          )
                        }
                        sx={{
                          border: '1px solid',
                          borderColor: day.overnightFlatRateClaimed
                            ? 'success.main'
                            : 'transparent',
                          ':active': { transform: 'scale(0.96)' },
                        }}
                      >
                        <HotelIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <span />
                  )}

                  <Typography
                    className="monospace whitespace-nowrap"
                    sx={{ fontSize: '13px', textAlign: 'right' }}
                  >
                    {`${numberToString(computed(day.date).dayTotal ?? 0)} ${currencySymbol}`}
                  </Typography>

                  <Tooltip title={t('Delete')}>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(index)}
                      sx={{ ':hover': { color: colors.error } }}
                    >
                      <DeleteIcon sx={{ fontSize: '16px' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {(field.preview?.warnings || []).length > 0 && (
          <Box className="flex flex-col gap-1 px-3 pb-2">
            {(field.preview?.warnings || []).map((warning) => (
              <Typography
                key={`${warning.code}-${warning.date}`}
                variant="body2"
                sx={{ color: colors.warning }}
              >
                {warning.message}
              </Typography>
            ))}
          </Box>
        )}

        <Box
          className="flex items-center gap-2 flex-wrap px-3 py-2"
          sx={{
            borderTop: `1px solid ${colors.selected}`,
            backgroundColor: colors.bgInput,
          }}
        >
          <Button size="small" startIcon={<AddCircleIcon />} onClick={handleAdd}>
            {t('Add day')}
          </Button>
          <Typography variant="caption" className="ml-auto opacity-60">
            {t('Days are suggested from the travel log')}
          </Typography>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default TypeTravelExpenseDays;
